import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { useQuery, useMutation } from 'react-query';
import { getAll as getAllResearchFields } from 'apis/researchFields.api';
import { getAll as getAllProposalPrograms } from 'apis/proposalPrograms.api';
import { getProposalById, updateProposal, uploadProposalFile } from 'apis/proposals.api';
import { toast } from 'layout';
import { TOAST } from 'constant';
import Loading from 'components/Loading';
import { toNonAccentVietnamese } from 'utils/func';
import ProposalForm from '../../components/ProposalForm/index';
import { proposalValidationSchema } from '../../validation';

const formOptions = { resolver: yupResolver(proposalValidationSchema) };

export default function EditProposal() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { proposalId } = useParams();

  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState();
  const [formData, setFormData] = useState(null);
  const [action, setAction] = useState('save');

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    setValue,
    reset,
    watch,
  } = useForm({ ...formOptions, mode: 'onChange' });

  const {
    data: proposalDetailData,
    refetch,
    isLoading,
  } = useQuery(['proposalDetail', proposalId], () => getProposalById(proposalId), {
    enabled: !!proposalId,
  });

  const { data: researchFieldsData } = useQuery(
    ['projectProposalResearchFields'],
    getAllResearchFields
  );

  const { data: programsData } = useQuery(['proposalPrograms'], () =>
    getAllProposalPrograms({ first: 0, rowsPerPage: 100 })
  );

  const researchFields = useMemo(
    () =>
      researchFieldsData?.data.map((e) => ({
        id: e.id,
        name: e.code ? t(`researchFields.${e.code}`) : e.name,
        code: e.code,
      })) ?? [],
    [researchFieldsData, t]
  );

  const programs = useMemo(
    () =>
      programsData?.data?.data?.map((e) => ({
        id: e.id,
        name: e.name,
        code: e.code,
      })) ?? [],
    [programsData]
  );

  useEffect(() => {
    if (proposalDetailData?.data?.data) {
      const data = proposalDetailData?.data?.data;
      setSelectedProgramId(data.proposalProgramId);
      const owner = {
        ownerId: data?.ownerId,
        email: data?.owner?.email,
        contact: {
          email: data?.owner?.email,
        },
        fullname: data?.owner?.fullname,
        gender: data?.owner?.scientificProfile?.gender,
        academicRank: data?.owner?.scientificProfile?.academicRank,
        academicDegree: data?.owner?.scientificProfile?.academicDegree,
        organizationId: data?.owner?.organizationId,
        specificOrganizationName: data?.owner?.specificOrganizationName,
      };
      reset({
        vietnameseName: data.vietnameseName,
        description: data.description,
        yearProposed: data.yearProposed,
        target: data.target,
        type: data.type,
        projectType: data.projectType,
        researchFieldIds: data.proposalResearchFields.map(
          ({ researchField, researchFieldId }) => researchField?.id ?? researchFieldId
        ),
        file: data.attachmentFileUrl,
        owner,
      });
    }
  }, [proposalDetailData, setValue]);

  const { mutate: updateProposalMutate } = useMutation(updateProposal);

  const { mutate: uploadMutate } = useMutation(uploadProposalFile);

  const handleSubmitProposal = (paramData) => {
    const dataToUse = { ...formData, ...paramData };
    const { owner, ...restData } = dataToUse;
    const finalData = {
      ...restData,
      ownerId: owner?.ownerId,
      proposalId,
      proposalResearchFields: dataToUse.researchFieldIds.map((researchFieldId) => ({
        researchFieldId,
      })),
      ...(selectedProgramId && { proposalProgramId: selectedProgramId }),
      ...(action === 'submit' && { status: 'submitted' }),
    };

    updateProposalMutate(
      { id: proposalDetailData?.data?.data?.id, data: finalData },
      {
        onSuccess: () => {
          setIsSubmitModalOpen(false);

          if (dataToUse?.file && dataToUse?.file instanceof File && action === 'save') {
            const formFileData = new FormData();
            formFileData.append('proposalId', proposalDetailData?.data?.data?.id);

            // eslint-disable-next-line no-unused-vars
            const newFileName = `R01-DX-${
              // eslint-disable-next-line no-nested-ternary
              finalData.type === 'topic' ? 'DT' : finalData.type === 'project' ? 'DA' : 'SXTN'
            }-${finalData.yearProposed}-${toNonAccentVietnamese(owner.fullname)}.pdf`;

            const renamedFile = new File([dataToUse?.file], newFileName, {
              type: dataToUse?.file.type,
            });

            formFileData.append('proposalFile', renamedFile);

            uploadMutate(
              { id: proposalDetailData?.data?.data?.id, data: formFileData },
              {
                onSuccess: () => {
                  toast(TOAST.SUCCESS, t('proposal.create.successEditMessage'));
                  refetch();
                },
                onError: () => {
                  toast(TOAST.ERROR, t('proposal.create.errorUploadFile'));
                },
              }
            );
          } else if (action === 'submit') {
            toast(TOAST.SUCCESS, t('proposal.create.successSubmitMessage'));
          } else {
            toast(TOAST.SUCCESS, t('proposal.create.successEditMessage'));
          }
          reset();
          refetch();
        },
        onError: (data) => {
          if (data?.response?.data?.code === 'proposalAlreadyExists') {
            toast(TOAST.ERROR, t('proposal.create.proposalAlreadyExists'));
          }
          if (data?.response?.data?.code === 'proposalProgramNotInTimeRange') {
            toast(TOAST.ERROR, t('proposal.create.proposalProgramNotInTimeRange'));
          }
        },
      }
    );
  };

  const onSubmit = (data) => {
    setFormData(data);
    if (action === 'submit') {
      setIsSubmitModalOpen(true);
    } else {
      handleSubmitProposal(data);
    }
  };

  return (
    <>
      {isLoading && <Loading />}
      <div className="card relative">
        <form onSubmit={handleSubmit(onSubmit)}>
          <ProposalForm
            disabled={proposalDetailData?.data?.status === 'submitted'}
            control={control}
            errors={errors}
            researchFields={researchFields}
            proposalDetailData={proposalDetailData?.data?.data}
            watch={watch}
            setValue={setValue}
          />
          <div
            className="text-right mt-4"
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '14px' }}
          >
            <Button
              label={t('proposal.create.cancel')}
              className="w-7rem"
              severity="danger"
              onClick={() => navigate('/proposals')}
            />
            {proposalDetailData?.data?.data?.status === 'new' && (
              <>
                <Button
                  label={t('proposal.create.save')}
                  className="w-7rem"
                  severity="info"
                  type="submit"
                  onClick={() => {
                    setAction('save');
                  }}
                  disabled={!isDirty || Object.keys(dirtyFields).length === 0}
                />
                <Button
                  label={t('proposal.create.submit')}
                  className="w-7rem"
                  severity="success"
                  onClick={() => {
                    setAction('submit');
                  }}
                  type="submit"
                  disabled={isDirty && Object.keys(dirtyFields).length > 0}
                />
              </>
            )}
          </div>
        </form>

        <Dialog
          header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
          visible={isSubmitModalOpen}
          position="center"
          style={{ width: '400px' }}
          onHide={() => setIsSubmitModalOpen(false)}
          draggable={false}
        >
          <label htmlFor="programId" className="inline-block mb-2">
            {t('projectProposal.review.program')}{' '}
            <span className="text-red-500" style={{ fontWeight: 900 }}>
              *
            </span>
          </label>

          <Dropdown
            id="proposalProgramId"
            value={selectedProgramId}
            options={programs}
            onChange={(e) => setSelectedProgramId(e.value)}
            optionLabel="name"
            optionValue="id"
            showClear
            placeholder={t('proposal.create.selectProgram')}
            style={{ width: '100%' }}
          />

          <div
            className="text-right mt-4"
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '14px' }}
          >
            <Button
              label={t('proposal.create.cancel')}
              className="w-7rem"
              severity="danger"
              onClick={() => setIsSubmitModalOpen(false)}
            />
            <Button
              label={t('proposal.create.oke')}
              className="w-7rem"
              severity="success"
              onClick={() => {
                handleSubmitProposal();
              }}
              disabled={!selectedProgramId}
            />
          </div>
        </Dialog>
      </div>
    </>
  );
}
